import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useVisitorCity } from '@/hooks/useVisitorCity';
import { getArticle, getArticleUpper } from '@/utils/grammar';

const PROFESSIONS = [
  /* ── Row 1: Original 8 ── */
  {
    name: 'PLUMBER', slug: 'plumbers',
    subtext: 'When a homeowner asks AI for a plumber, we make sure your name is in the running.',
    photo: '/images/professions/plumbers.png',
  },
  {
    name: 'STYLIST', slug: 'stylists',
    subtext: "When a client asks AI for a stylist, we make sure your salon is in the running.",
    photo: '/images/professions/stylists.png',
  },
  {
    name: 'DENTIST', slug: 'dentists',
    subtext: 'When a patient asks AI for a dentist nearby, we make sure your practice is in the running.',
    photo: '/images/professions/dentists.png',
  },
  {
    name: 'PERSONAL TRAINER', slug: 'personal-trainers',
    subtext: "When someone asks AI for a trainer, we make sure your name is in the running.",
    photo: '/images/professions/personal-trainers.png',
  },
  {
    name: 'ELECTRICIAN', slug: 'electricians',
    subtext: 'When a homeowner needs an electrician, AI should send them to you.',
    photo: '/images/professions/electricians.png',
  },
  {
    name: 'PHOTOGRAPHER', slug: 'photographers',
    subtext: 'When a couple asks AI for a photographer, we make sure your portfolio is in the running.',
    photo: '/images/professions/photographers.png',
  },
  {
    name: 'LANDSCAPER', slug: 'landscapers',
    subtext: 'When a property owner asks AI for landscaping, we make sure your work is in the running.',
    photo: '/images/professions/landscapers.png',
  },
  {
    name: 'HVAC TECH', slug: 'hvac-technicians',
    subtext: "When someone's furnace breaks, we make sure your name is in the running.",
    photo: '/images/professions/hvac-technicians.png',
  },
  /* ── Row 2: Addendum 5.1 ── */
  {
    name: 'MECHANIC', slug: 'mechanics',
    subtext: 'When a car owner asks AI for a trusted mechanic, we make sure your shop is in the running.',
    photo: '/images/professions/mechanics.png',
  },
  {
    name: 'MASSAGE THERAPIST', slug: 'massage-therapists',
    subtext: 'When someone asks AI for a massage therapist, we make sure your practice is in the running.',
    photo: '/images/professions/massage-therapists.png',
  },
  {
    name: 'PHYSICAL THERAPIST', slug: 'physical-therapists',
    subtext: 'When a patient asks AI for a physical therapist, we make sure your clinic is in the running.',
    photo: '/images/professions/physical-therapists.png',
  },
  {
    name: 'REAL ESTATE AGENT', slug: 'real-estate-agents',
    subtext: 'When a buyer asks AI for a real estate agent, we make sure your name is in the running.',
    photo: '/images/professions/real-estate-agents.png',
  },
  {
    name: 'DOG GROOMER', slug: 'dog-groomers',
    subtext: 'When a pet owner asks AI for a groomer, we make sure your salon is in the running.',
    photo: '/images/professions/dog-groomers.png',
  },
  {
    name: 'ROOFER', slug: 'roofers',
    subtext: 'When a homeowner needs a roofer, we make sure your name is in the running.',
    photo: '/images/professions/roofers.png',
  },
  {
    name: 'CHIROPRACTOR', slug: 'chiropractors',
    subtext: 'When someone asks AI for a chiropractor, we make sure your practice is in the running.',
    photo: '/images/professions/chiropractors.png',
  },
  {
    name: 'TATTOO ARTIST', slug: 'tattoo-artists',
    subtext: 'When someone asks AI for a tattoo artist, we make sure your work is in the running.',
    photo: '/images/professions/tattoo-artists.png',
  },
  /* ── Row 3: Addendum 5.2 — 8 NEW High-Volume ── */
  {
    name: 'LAWYER', slug: 'lawyers',
    subtext: 'When someone needs a lawyer, we make sure your firm is in the running.',
    photo: '/images/professions/lawyers.png',
  },
  {
    name: 'HOUSE CLEANER', slug: 'house-cleaners',
    subtext: 'When someone asks AI for a house cleaner, we make sure your service is in the running.',
    photo: '/images/professions/house-cleaners.png',
  },
  {
    name: 'ACCOUNTANT', slug: 'accountants',
    subtext: 'When a small business needs an accountant, we make sure your practice is in the running.',
    photo: '/images/professions/accountants.png',
  },
  {
    name: 'PAINTER', slug: 'painters',
    subtext: 'When a homeowner asks AI for a painter, we make sure your name is in the running.',
    photo: '/images/professions/painters.png',
  },
  {
    name: 'HANDYMAN', slug: 'handymen',
    subtext: 'When someone needs a handyman, we make sure your name is in the running.',
    photo: '/images/professions/handymen.png',
  },
  {
    name: 'VETERINARIAN', slug: 'veterinarians',
    subtext: 'When a pet owner asks AI for a vet, we make sure your clinic is in the running.',
    photo: '/images/professions/veterinarians.png',
  },
  {
    name: 'PEST CONTROL', slug: 'pest-control',
    subtext: 'When someone has a pest problem, we make sure your name is in the running.',
    photo: '/images/professions/pest-control.png',
  },
  {
    name: 'PET TRAINER', slug: 'pet-trainers',
    subtext: 'When a pet owner asks AI for a dog trainer, we make sure your program is in the running.',
    photo: '/images/professions/pet-trainers.png',
  },
  /* ── Row 3 Extension: Profession Groups ── */
  {
    name: 'PHYSICIAN', slug: 'physicians',
    subtext: 'When a patient asks AI for a doctor, we make sure your practice is in the running.',
    photo: 'https://images.unsplash.com/photo-1758691462858-f1286e5daf40?w=1600&q=80&fit=crop',
  },
  {
    name: 'ARCHITECT', slug: 'architects',
    subtext: 'When a client asks AI for an architect, we make sure your studio is in the running.',
    photo: 'https://images.unsplash.com/photo-1764740109279-c7a8abd78821?w=1600&q=80&fit=crop',
  },
  {
    name: 'INTERIOR DESIGNER', slug: 'interior-designers',
    subtext: 'When a homeowner asks AI for a designer, we make sure your studio is in the running.',
    photo: 'https://images.unsplash.com/photo-1620288840365-bf3f7dbd537e?w=1600&q=80&fit=crop',
  },
];

const ROW_1 = PROFESSIONS.slice(0, 8);
const ROW_2 = PROFESSIONS.slice(8, 16);
const ROW_3 = PROFESSIONS.slice(16, 27);

export default function ProfessionShowcase() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const sectionRef = useRef(null);
  const timerRef = useRef(null);
  const pauseTimeout = useRef(null);
  const mobileStripRef = useRef(null);
  const visitorCity = useVisitorCity('your area');

  // Smart start: detect profession from UTM
  useEffect(() => {
    const utmProf = searchParams.get('prof') || searchParams.get('profession');
    if (utmProf) {
      const idx = PROFESSIONS.findIndex(
        (p) => p.name.toLowerCase().replace(/\s+/g, '-') === utmProf.toLowerCase().replace(/\s+/g, '-')
      );
      if (idx >= 0) setActive(idx);
    }
  }, [searchParams]);

  const startAutoRotation = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % PROFESSIONS.length);
    }, 5000);
  }, []);

  // Auto-rotation with IntersectionObserver
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !paused) {
          startAutoRotation();
        } else {
          if (timerRef.current) clearInterval(timerRef.current);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      if (timerRef.current) clearInterval(timerRef.current);
      if (pauseTimeout.current) clearTimeout(pauseTimeout.current);
    };
  }, [paused, startAutoRotation]);

  // Auto-scroll active profession into view on mobile
  useEffect(() => {
    if (!mobileStripRef.current) return;
    const btn = mobileStripRef.current.querySelector(`[data-prof-idx="${active}"]`);
    if (btn) {
      btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [active]);

  const handleProfessionClick = (index) => {
    setActive(index);
    setPaused(true);
    if (timerRef.current) clearInterval(timerRef.current);
    if (pauseTimeout.current) clearTimeout(pauseTimeout.current);
    pauseTimeout.current = setTimeout(() => {
      setPaused(false);
      startAutoRotation();
    }, 8000);
  };

  const prof = PROFESSIONS[active];

  return (
    <section
      ref={sectionRef}
      className="relative flex flex-col items-center justify-center overflow-hidden"
      style={{ minHeight: '100vh' }}
      data-testid="profession-showcase"
    >
      {/* Background photos with crossfade */}
      <AnimatePresence mode="sync">
        <motion.div
          key={active}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
        >
          <img
            src={prof.photo}
            alt={prof.name}
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.4) saturate(0.85)' }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Dark gradient overlay for text readability */}
      <div
        className="absolute inset-0 z-[1]"
        style={{
          background:
            'linear-gradient(to bottom, rgba(44,37,32,0.4) 0%, rgba(44,37,32,0.15) 40%, rgba(44,37,32,0.55) 100%)',
        }}
      />

      {/* Centered content */}
      <div className="relative z-[2] text-center px-6 flex-1 flex flex-col items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4 }}
          >
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 300,
                textTransform: 'uppercase',
                letterSpacing: '0.2em',
                color: '#F0EBE3',
                fontSize: 'clamp(28px, 4vw, 48px)',
                lineHeight: 1.15,
              }}
              data-testid="profession-headline"
            >
              GET CHOSEN FIRST AS {getArticleUpper(prof.name)} {prof.name}
            </h2>
            <p
              className="mt-4 mx-auto"
              style={{
                color: 'rgba(240,235,227,0.6)',
                fontSize: '14px',
                fontFamily: "'DM Sans', sans-serif",
                maxWidth: '480px',
              }}
              data-testid="profession-subtext"
            >
              {prof.subtext.replace(/asks AI for/i, `asks AI in ${visitorCity} for`)}
            </p>
            <button
              onClick={() => navigate(`/for/${prof.slug}`)}
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 transition-all hover:scale-105"
              style={{
                backgroundColor: 'rgba(240,235,227,0.15)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(240,235,227,0.35)',
                color: '#F0EBE3',
                fontSize: '13px',
                fontFamily: "'DM Sans', sans-serif",
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                fontWeight: 500,
              }}
              data-testid="profession-explore-link"
            >
              Explore {prof.name.toLowerCase()} &rarr;
            </button>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ── Three-Row Profession Selector Bar ── */}
      <div className="relative z-[2] w-full pb-24 pt-4" data-testid="profession-selector-bar">
        {/* Desktop: Three rows of 8 */}
        <div className="hidden md:flex flex-col items-center gap-2">
          {[ROW_1, ROW_2, ROW_3].map((row, rowIdx) => (
            <div
              key={rowIdx}
              className={`flex items-center justify-center ${rowIdx === 2 ? 'gap-[14px]' : 'gap-6'}`}
              style={{ maxWidth: '720px' }}
              data-testid={`profession-row-${rowIdx + 1}`}
            >
              {row.map((p, i) => {
                const globalIdx = rowIdx === 0 ? i : rowIdx === 1 ? 8 + i : 16 + i;
                return (
                  <button
                    key={p.name}
                    onClick={() => handleProfessionClick(globalIdx)}
                    className="whitespace-nowrap transition-all duration-300 pb-1 flex-shrink-0"
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      color:
                        globalIdx === active
                          ? '#F0EBE3'
                          : 'rgba(240,235,227,0.35)',
                      borderBottom:
                        globalIdx === active
                          ? '2px solid #B8985A'
                          : '2px solid transparent',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      if (globalIdx !== active)
                        e.target.style.color = 'rgba(240,235,227,0.6)';
                    }}
                    onMouseLeave={(e) => {
                      if (globalIdx !== active)
                        e.target.style.color = 'rgba(240,235,227,0.35)';
                    }}
                    data-testid={`profession-tab-${globalIdx}`}
                  >
                    {p.name}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Mobile: Single horizontally scrollable strip */}
        <div className="md:hidden relative">
          <div
            className="absolute left-0 top-0 bottom-0 w-8 z-10 pointer-events-none"
            style={{
              background:
                'linear-gradient(to right, rgba(44,37,32,0.7), transparent)',
            }}
          />
          <div
            className="absolute right-0 top-0 bottom-0 w-8 z-10 pointer-events-none"
            style={{
              background:
                'linear-gradient(to left, rgba(44,37,32,0.7), transparent)',
            }}
          />
          <div
            ref={mobileStripRef}
            className="flex items-center gap-5 overflow-x-auto px-10 no-scrollbar"
            data-testid="profession-mobile-strip"
          >
            {PROFESSIONS.map((p, i) => (
              <button
                key={p.name}
                data-prof-idx={i}
                onClick={() => handleProfessionClick(i)}
                className="whitespace-nowrap transition-all duration-300 pb-1 flex-shrink-0"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color:
                    i === active
                      ? '#F0EBE3'
                      : 'rgba(240,235,227,0.35)',
                  borderBottom:
                    i === active
                      ? '2px solid #B8985A'
                      : '2px solid transparent',
                }}
                data-testid={`profession-mobile-tab-${i}`}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
